import { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  onSnapshot, 
  serverTimestamp 
} from 'firebase/firestore';
import './InOut.css';
import { motion, AnimatePresence } from 'framer-motion';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Audio Imports from Assets
import createSound from './assets/create.mp3';
import editSound from './assets/edit.mp3';
import magicSound from './assets/magic.mp3';
import deleteSound from './assets/delete.mp3';

export default function InOutSection() {
  const [items, setItems] = useState([]);
  const [input, setInput] = useState('');
  const [side, setSide] = useState('in');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
   const [showLimitModal, setShowLimitModal] = useState(false);
     const LimitModal = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="modal-overlay">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="limit-modal-content"
        >
          <div className="modal-icon">⏳</div>
          <h2>Just use your brains bro</h2>
          <p>
            It ain't that tuff to type your own <b>INs/OUTs</b>. 
            Don't be so cooked 😭
          </p>
          <button onClick={onClose} className="modal-close-btn">
            UNDERSTOOD
          </button>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);
  const handleAiGenerate = async () => {
     const user = auth.currentUser;
      if (!user) return alert("Please log in first!");
       await user.getIdToken(true);
    magicAudio.current.currentTime = 0;
  magicAudio.current.play().catch(() => {});
    setIsGenerating(true);
    try {
      const functions = getFunctions();
      const genInOut = httpsCallable(functions, 'generateInOut');
      const result = await genInOut({
  type: side,
  userId: auth.currentUser.uid
});
      if (Array.isArray(result.data)) {
  setAiSuggestions(result.data);
} else {
  console.error("Unexpected AI response:", result.data);
  setAiSuggestions([]);
}
    } catch (err) {
      console.error(err);
  const isLimitError =
    err.code === 'resource-exhausted' ||
    err.details?.includes('LIMIT_REACHED') ||
    err.message?.includes('LIMIT_REACHED');

  if (isLimitError) {
    if (window.navigator.vibrate) {
      window.navigator.vibrate([100, 50, 100]);
    }
    setShowLimitModal(true);
  } else {
    console.error("Unexpected AI error:", err);}
    }  finally {
      setIsGenerating(false);
    }
  };
  const acceptAiItem = async (text, index) => {
    playAudio('create');
    const inOutRef = collection(db, 'users', auth.currentUser.uid, 'inOut');
    await addDoc(inOutRef, {
      text: text,
      type: side,
      createdAt: serverTimestamp()
    });
    setAiSuggestions(prev => prev.filter((_, i) => i !== index));
  };
  
  // State for Inline Editing
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // Audio References
  const audioCreate = useRef(new Audio(createSound));
  const audioEdit = useRef(new Audio(editSound));
  const audioDelete = useRef(new Audio(deleteSound));
  const magicAudio = useRef(new Audio(magicSound));

  const playAudio = (type) => {
    const audio = type === 'create' ? audioCreate : type === 'edit' ? audioEdit : audioDelete;
    audio.current.currentTime = 0;
    audio.current.play().catch(e => console.log("Audio blocked", e));
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    const inOutRef = collection(db, 'users', auth.currentUser.uid, 'inOut');
    const q = query(inOutRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  const addItem = async () => {
    if (!input.trim()) return;
    playAudio('create');
    try {
      const inOutRef = collection(db, 'users', auth.currentUser.uid, 'inOut');
      await addDoc(inOutRef, {
        text: input,
        type: side,
        createdAt: serverTimestamp()
      });
      setInput('');
    } catch (err) { console.error(err); }
  };

  const deleteItem = async (id) => {
    playAudio('delete');
    try {
      await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'inOut', id));
    } catch (err) { console.error(err); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    playAudio('edit');
    try {
      const docRef = doc(db, 'users', auth.currentUser.uid, 'inOut', id);
      await updateDoc(docRef, { text: editText });
      setEditingId(null);
    } catch (err) { console.error(err); }
  };

  const ItemCard = ({ item }) => (
    <motion.div 
      layout 
      className="item-card"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
    >
      {editingId === item.id ? (
        <div className="edit-mode">
          <input 
            value={editText} 
            onChange={(e) => setEditText(e.target.value)}
            className="edit-input"
            autoFocus
          />
          <button onClick={() => saveEdit(item.id)}>✓</button>
        </div>
      ) : (
        <>
          <span className="item-text">{item.text}</span>
          <div className="item-actions">
            <button className="action-btn edit" onClick={() => startEdit(item)}>✎</button>
            <button className="action-btn del" onClick={() => deleteItem(item.id)}>✕</button>
          </div>
        </>
      )}
    </motion.div>
  );

  return (
    <div className="inout-grid">
      <div className="input-box-main">
        <input 
          value={input} 
          onChange={e => setInput(e.target.value)} 
          placeholder={`whAt's ${side} for 2026?`} 
          className="mono-input"
        />
        <select value={side} onChange={e => setSide(e.target.value)}>
          <option value="in">IN</option>
          <option value="out">OUT</option>
        </select>
        <button onClick={addItem} className="lock-btn">LOCK</button>
        <button 
          onClick={handleAiGenerate} 
          disabled={isGenerating}
          className={`ai-gen-btn ${side}`}
        >
          {isGenerating ? "..." : `AI ${side.toUpperCase()}`}
        </button>
      
      </div>
      <div className="ai-suggestion-shelf">
        <AnimatePresence>
          {aiSuggestions.map((text, idx) => (
            <motion.div 
              key={idx}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className={`suggestion-pill ${side}`}
            >
              <span>{text}</span>
              <div className="pill-actions">
                <button onClick={() => acceptAiItem(text, idx)}>✓</button>
                <button onClick={() => setAiSuggestions(prev => prev.filter((_, i) => i !== idx))}>✕</button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="columns">
        <div className="col in">
          <h2>INS</h2>
          <AnimatePresence>
            {items.filter(i => i.type === 'in').map(item => (
              <ItemCard key={item.id} item={item} />
            ))}
          </AnimatePresence>
        </div>

        <div className="col out">
          <h2>OUTS</h2>
          <AnimatePresence>
            {items.filter(i => i.type === 'out').map(item => (
              <ItemCard key={item.id} item={item} />
            ))}
          </AnimatePresence>
        </div>
      </div>
       <LimitModal 
  isOpen={showLimitModal} 
  onClose={() => setShowLimitModal(false)} 
/>
    </div>
  );
}